import { useState, useEffect } from "react";
import { FaPlay, FaLock, FaEye } from "react-icons/fa";
import { getRequest } from "../helpers/api.helper";
import { UPLOAD_URL } from "../utils/constants";
import Gnavbar from "../components/Gnavbar";
import BottomNavbar from "../components/BottomNavbar";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useNavigate } from "react-router-dom";
import { hasActiveMembership } from "../utils/bureau";
import { toast } from "react-toastify";

type VideoType = {
  thumbnail: string;
  id: string;
  videoLink: string;
  title: string;
  description: string;
  status: string;
};

const HowToUse = () => {
  const [videos, setVideos] = useState<VideoType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<VideoType | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const navigate = useNavigate();
  const isPaidAccount = hasActiveMembership(bureauInfo?.paymentStatus);

  useEffect(() => {
    fetchVideos();
  }, []);

  useEffect(() => {
    if (bureauInfo && !isPaidAccount) {
      navigate("/admin/membership/plans", { replace: true });
    }
  }, [bureauInfo, isPaidAccount, navigate]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await getRequest("/bureau/videos");
      if (response.success) {
        setVideos(response.data);
      }
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const normalizeVideoUrl = (url?: string) => {
    if (!url) return "";

    const trimmed = url.trim();
    if (!trimmed) return "";

    if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
      return `https://www.youtube.com/watch?v=${trimmed}`;
    }

    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    return `https://${trimmed}`;
  };

  const getVideoId = (url?: string) => {
    const normalizedUrl = normalizeVideoUrl(url);
    if (!normalizedUrl) return null;

    // Handle various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
      /youtu\.be\/([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
      const match = normalizedUrl.match(pattern);
      if (match && match[1] && match[1].length === 11) {
        return match[1];
      }
    }

    return null;
  };

  const getEmbedUrl = (videoUrl: string) => {
    const videoId = getVideoId(videoUrl);
    return videoId
      ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0`
      : "";
  };

  const getThumbnailUrl = (videoUrl: string) => {
    const videoId = getVideoId(videoUrl);
    return videoId
      ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
      : "";
  };

  const getUploadedThumbnailUrl = (thumbnail?: string) => {
    if (!thumbnail) return "";
    if (/^https?:\/\//i.test(thumbnail)) return thumbnail;
    return `${UPLOAD_URL}/business/howto_videos/${thumbnail}`;
  };

  const handleVideoClick = (video: VideoType) => {
    if (!isPaidAccount) {
      navigate("/admin/membership/plans", { state: { fromShare: true } });

      return;
    }

    const embedUrl = getEmbedUrl(video.videoLink);

    if (!embedUrl) {
      const fallbackUrl = normalizeVideoUrl(video.videoLink);

      if (!fallbackUrl) {
        toast.error("Video link is not available.");
        return;
      }

      window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      return;
    }

    setSelectedVideo(video);
    setShowVideoModal(true);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setSelectedVideo(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (bureauInfo && !isPaidAccount) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Gnavbar />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Free Account Warning */}
        {bureauInfo && !isPaidAccount && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <FaLock className="text-yellow-600 mr-2" />
              <div>
                <h3 className="text-yellow-800 font-semibold">
                  Premium Feature
                </h3>
                <p className="text-yellow-700 text-sm">
                  Video tutorials are available for paid members only. Upgrade
                  your plan to access all video guides.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Video Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Video Tutorials
          </h2>

          {/* Videos Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div
                key={video.id}
                className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
                  isPaidAccount ? "cursor-pointer" : "opacity-60"
                }`}
                onClick={() => handleVideoClick(video)}
              >
                {/* Thumbnail */}
                <div className="relative h-48 bg-gray-200">
                  <img
                    src={
                      video.thumbnail
                        ? getUploadedThumbnailUrl(video.thumbnail)
                        : getThumbnailUrl(video.videoLink) ||
                          "/default-thumbnail.jpg"
                    }
                    alt={video.title}
                    className="w-full h-full object-cover"
                    onError={(e: any) => {
                      e.target.src =
                        getThumbnailUrl(video.videoLink) ||
                        "/default-thumbnail.jpg";
                    }}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                    {isPaidAccount ? (
                      <FaEye className="text-white text-3xl opacity-80" />
                    ) : (
                      <FaLock className="text-white text-3xl opacity-80" />
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2">
                    {video.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {video.description}
                  </p>

                  {/* Status Badge */}
                  <div className="flex justify-between items-center">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        video.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {video.status === "active" ? "Active" : "Inactive"}
                    </span>
                    {!isPaidAccount && (
                      <span className="text-xs text-gray-500 flex items-center">
                        <FaLock className="mr-1" /> Premium
                      </span>
                    )}
                  </div>

                  {/* Watch Button - Only show for paid members */}
                  {isPaidAccount && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVideoClick(video);
                      }}
                      className="mt-3 w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <FaPlay className="text-sm" />
                      Watch Video
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {videos.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📹</div>
              <p className="text-gray-500 text-lg mb-4">
                No video tutorials available
              </p>
              <p className="text-gray-400">
                Check back later for helpful video guides!
              </p>
            </div>
          )}
        </div>

        {/* Video Modal */}
        {showVideoModal && selectedVideo && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-xl font-semibold text-gray-800">
                  {selectedVideo.title}
                </h2>
                <button
                  onClick={closeVideoModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="p-4">
                {/* Video Player */}
                <div
                  className="relative w-full"
                  style={{ paddingBottom: "56.25%" }}
                >
                  <iframe
                    src={getEmbedUrl(selectedVideo.videoLink)}
                    title={selectedVideo.title}
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>

                {/* Video Description */}
                <div className="mt-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {selectedVideo.title}
                  </h3>
                  <p className="text-gray-600">{selectedVideo.description}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNavbar />
    </div>
  );
};

export default HowToUse;
